/**
 * AdminRoomDetailPage — the read-only workspace for one room (D4c2), at
 * ``/admin/rooms/:roomId``.
 *
 * A room was reachable from three places — the room map, a task, a
 * report — and openable from none. Every surface could name it and then
 * stopped. This is the page those names now point at, and the reason
 * D4c1's room row stops being plain text.
 *
 * Layout follows the report detail page: breadcrumb, a header card, then
 * two columns on desktop — Belegung on the left, Zimmerdetails and
 * Verknüpfungen on the right — and a single stack below ``lg``.
 *
 * **The ROOM is read-only, deliberately.** There is no edit button:
 * rooms are edited in ``UnitRoomModal`` on the unit page, where the unit
 * context that validates the change already exists. Adding a second
 * editing surface here would be a second place for that validation to
 * drift. D4c3's report section is not an exception to that — it edits
 * reports, never the room.
 *
 * D4c3 adds "Rapporte des Zimmers" below the two columns: the same
 * ``EntityWorkReportsTab`` the five other entity pages mount, given the
 * room scope. No second component, no second summary path — this page
 * only says WHICH entity.
 *
 * D4d adds "Operative Aktivität" below that: the same
 * ``EntityActivityFeed`` seven other surfaces mount. Tasks and reports
 * only — see the comment at the mount for why this page shows no change
 * history and does not pretend to.
 *
 * The page is an orchestrator (CLAUDE.md Regel 48) — it fetches and
 * composes. Every card lives in ``rooms/RoomDetailCards`` and every
 * display rule in ``rooms/roomDetailHelpers``.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DoorOpen } from "lucide-react";

import { fetchAdminRoomById } from "../../api/adminData";
import EntityActivityFeed from "./activity/EntityActivityFeed";
import EntityWorkReportsTab from "./tasks/EntityWorkReportsTab";
import {
  RoomFactsCard,
  RoomOccupancyCard,
  RoomParentsCard,
  PlanningStateBadge,
} from "./rooms/RoomDetailCards";
import { roomTitle } from "./rooms/roomDetailHelpers";

/** Rooms are listed on the Zimmer overview, so the breadcrumb points
 * where the user actually came from. */
export const ROOMS_OVERVIEW_PATH = "/admin/rooms";

const SKELETON_CARD =
  "rounded-[12px] border border-border bg-card p-5 shadow-sm " +
  "dark:border-[#1e2130] dark:bg-[#13161f]";
const SKELETON_BAR = "animate-pulse rounded bg-muted dark:bg-[#1e2130]";

function Breadcrumb({ current }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="mb-4 text-[11px] text-muted-foreground dark:text-[#4a5070]"
    >
      <span>Portfolio</span>
      <span className="mx-2" aria-hidden="true">
        /
      </span>
      <Link
        to={ROOMS_OVERVIEW_PATH}
        className="rounded-[4px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:text-[#94a3b8]"
      >
        Zimmer
      </Link>
      <span className="mx-2" aria-hidden="true">
        /
      </span>
      <span aria-current="page" className="text-foreground dark:text-[#94a3b8]">
        {current}
      </span>
    </nav>
  );
}

/** Skeletons in the shape the page settles into, so nothing jumps when
 * the data lands. */
function DetailSkeleton() {
  return (
    <div aria-hidden="true">
      <div className={`${SKELETON_CARD} mb-4 flex items-center gap-4`}>
        <div className={`${SKELETON_BAR} h-14 w-14 rounded-full`} />
        <div className="flex-1">
          <div className={`${SKELETON_BAR} h-6 w-2/5`} />
          <div className={`${SKELETON_BAR} mt-2 h-4 w-24`} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className={`${SKELETON_CARD} h-[260px]`} />
        </div>
        <div className="space-y-4">
          <div className={`${SKELETON_CARD} h-[300px]`} />
          <div className={`${SKELETON_CARD} h-[120px]`} />
        </div>
      </div>
    </div>
  );
}

/** Title, the room's own name, and its occupancy state at a glance. */
function RoomDetailHeader({ room }) {
  return (
    <section
      aria-labelledby="room-header-heading"
      className={`${SKELETON_CARD} mb-4 flex flex-wrap items-center gap-4`}
    >
      <span
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-border bg-muted/60 dark:border-[#1e2130] dark:bg-[#0d0f14]"
        aria-hidden="true"
      >
        <DoorOpen className="h-6 w-6 text-muted-foreground dark:text-[#4a5070]" />
      </span>
      <div className="min-w-0 flex-1">
        <h1
          id="room-header-heading"
          className="m-0 break-words text-[20px] font-semibold text-foreground dark:text-[#e2e4ed]"
        >
          {roomTitle(room)}
        </h1>
        <div className="mt-2">
          <PlanningStateBadge room={room} />
        </div>
      </div>
    </section>
  );
}

export default function AdminRoomDetailPage() {
  const { roomId } = useParams();

  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!roomId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await fetchAdminRoomById(roomId);
      setRoom(data);
    } catch (err) {
      // 404 covers both "no such room" and "belongs to another
      // organisation" — the backend answers identically for the two, and
      // so does this page. Telling them apart here would undo the
      // backend's refusal to confirm that an id names a real row.
      if (err?.status === 404) {
        setNotFound(true);
      } else {
        setError(err?.message || "Das Zimmer konnte nicht geladen werden.");
      }
      setRoom(null);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1400px]">
        <Breadcrumb current="Zimmerdetails" />
        <p role="status" className="sr-only">
          Zimmer wird geladen …
        </p>
        <DetailSkeleton />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mx-auto max-w-[1400px]">
        <Breadcrumb current="Zimmerdetails" />
        <div
          role="status"
          className="rounded-[12px] border border-dashed border-border bg-card p-8 text-center dark:border-[#1e2130] dark:bg-[#13161f]"
        >
          <p className="m-0 text-[14px] font-medium text-foreground dark:text-[#e2e4ed]">
            Das Zimmer wurde nicht gefunden oder du hast keinen Zugriff darauf.
          </p>
          <Link
            to={ROOMS_OVERVIEW_PATH}
            className="mt-4 inline-flex rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#c5cbe0]"
          >
            Zurück zur Zimmerübersicht
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-[1400px]">
        <Breadcrumb current="Zimmerdetails" />
        <div
          role="alert"
          className="rounded-[12px] border border-destructive/40 bg-destructive/10 p-6 dark:border-[#3a1f24] dark:bg-[#1e0d10]"
        >
          <p className="m-0 text-[13px] font-medium text-destructive dark:text-[#ff6b6b]">
            {error}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={load}
              className="rounded-[8px] border border-destructive/40 bg-transparent px-3 py-1.5 text-[12px] font-semibold text-destructive hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Erneut versuchen
            </button>
            <Link
              to={ROOMS_OVERVIEW_PATH}
              className="rounded-[8px] border border-border bg-transparent px-3 py-1.5 text-[12px] font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#c5cbe0]"
            >
              Zurück zur Zimmerübersicht
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px]">
      <Breadcrumb current="Zimmerdetails" />

      <RoomDetailHeader room={room} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <RoomOccupancyCard room={room} />
        </div>
        <div className="space-y-4">
          <RoomFactsCard room={room} />
          <RoomParentsCard room={room} />
        </div>
      </div>

      {/* D4c3. Full width under both columns rather than inside one: the
          section carries a table, and a table in half a column scrolls
          sideways on every laptop.

          ``room?.id`` and not the route param: this renders only once the
          room has loaded, and the id the server returned is the one its
          reports are filed under. A falsy id makes the hook fetch
          nothing at all, which is the honest behaviour for "no room". */}
      <div className="mt-4">
        <EntityWorkReportsTab scope="room" entityId={room?.id} />
      </div>

      {/* D4d. "Operative Aktivität", not "Verlauf": this is what people
          DID here — the tasks and reports linked to the room, including
          the ones reaching it through a tenancy. It is deliberately NOT
          the room's change history, which does not exist under a room id
          at all: create/update/delete are written on the parent unit's
          audit stream. Reading that stream would pull every sibling
          room's edits onto this page, so the feed says nothing about
          master data rather than something wrong — and the subtitle
          below says exactly that, instead of implying completeness. */}
      <div className="mt-4">
        <EntityActivityFeed
          entityType="room"
          entityId={room?.id}
          headingId="room-activity-heading"
          headingText="Operative Aktivität"
          description="Aufgaben und Rapporte zu diesem Zimmer"
        />
      </div>
    </div>
  );
}
